import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { Box, Divider, Menu, MenuItem, IconButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthToken, setAuthUser } from '../../../redux/reducers/authSlice';
import Iconify from '../../../components/Iconify';
import { authApiCall } from '../../../services/http';
import { endpoints } from '../../../config';

// ----------------------------------------------------------------------

UserMoreMenu.propTypes = {
  user: PropTypes.any,
  onMenuAction: PropTypes.func,
};

export default function UserMoreMenu({ user, onMenuAction }) {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);

  const authApiCallHookProps = {
    navigate,
    dispatch,
    setAuthUser,
    setAuthToken,
    authToken,
    redirectRoute: null,
  };

  const deleteUser = async () =>
    authApiCall({
      method: 'DELETE',
      endpoint: endpoints.users,
      path: `/${user.id}`,
      ...authApiCallHookProps,
    });

  const alterUser = async (action, userId) => {
    try {
      if (action === 'delete') {
        const accept = window.confirm(
          `Esta acción no se puede deshacer, se eliminarán todos los recursos de ${user.givenName} (${user.email}) de la base de datos ¿Estás seguro?`
        );
        if (accept) {
          await deleteUser(userId);
          onMenuAction(action, userId);
        }
      } else if (action === 'show-metrics') {
        onMenuAction(action, userId);
      } else if (action === 'edit-user') {
        onMenuAction(action, userId);
      }
    } catch (e) {
      console.error(e);
    }
    setIsOpen(false);
  };

  return (
    <>
      <IconButton ref={ref} onClick={() => setIsOpen(true)}>
        <Iconify icon="eva:more-vertical-fill" width={20} height={20} />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: { width: 300, maxWidth: '100%' },
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={async () => {
            await alterUser('show-metrics', user.id);
          }}
          component={RouterLink}
          to="#"
          sx={{ color: 'text.secondary' }}
        >
          <ListItemIcon>
            <Iconify icon="eva:activity-outline" width={24} height={24} />
          </ListItemIcon>
          <ListItemText primary="Consultar métricas" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>

        {/* Deshabilitado hasta que backend exponga PUT /admin-api/users/:id con auth de admin
            (givenName, email, phoneNumber, workspaceId). Cuando exista, quitar `disabled` y el Tooltip. */}
        <Tooltip
          title="Pendiente: backend tiene que exponer un endpoint admin para actualizar el usuario."
          placement="left"
        >
          <Box>
            <MenuItem
              disabled
              onClick={async () => {
                await alterUser('edit-user', user.id);
              }}
              component={RouterLink}
              to="#"
              sx={{ color: 'text.secondary' }}
            >
              <ListItemIcon>
                <Iconify icon="eva:edit-2-outline" width={24} height={24} />
              </ListItemIcon>
              <ListItemText primary="Editar usuario" primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
          </Box>
        </Tooltip>

        {/* Gestión de plan/suscripción: el cambio de plan ya no vive aquí (se quitaron los
            PUT /users/update-plan y /workspaces/update-plan). Ahora se gestiona en el dominio
            de Suscripciones. "Ver la sub" abre el drill-in (SubscriptionUsersDialog) igual que
            al hacer click sobre el plan/estado en la fila. */}
        <Divider />
        <Tooltip
          title={user.subscription?.id ? '' : 'Este usuario no tiene suscripción.'}
          placement="left"
          disableHoverListener={Boolean(user.subscription?.id)}
        >
          <Box>
            <MenuItem
              disabled={!user.subscription?.id}
              onClick={() => {
                setIsOpen(false);
                onMenuAction('open-subscription', user.id);
              }}
              sx={{ color: 'text.secondary' }}
            >
              <ListItemIcon>
                <Iconify icon="eva:credit-card-outline" width={24} height={24} />
              </ListItemIcon>
              <ListItemText primary="Ver la sub" primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
          </Box>
        </Tooltip>

        {/* Histórico: deep-link a la tabla de Suscripciones filtrada por este usuario. Ahí salen
            todas las que ha tenido (incluidas canceled/expired) y se pueden editar una a una.
            Buscamos por email (`searchBy=userEmail`) porque cubre tanto las subs de las que es
            TITULAR como aquellas en las que solo ocupa un asiento. Solo si no tuviera email caemos
            al filtro por id de titular, que se deja las de equipo fuera. */}
        <Tooltip
          title="Todas las subs de este usuario (histórico), como titular o como ocupante de un asiento."
          placement="left"
        >
          <MenuItem
            onClick={() => {
              setIsOpen(false);
              navigate(
                user.email
                  ? `/dashboard/subscriptions?searchBy=userEmail&search=${encodeURIComponent(user.email)}`
                  : `/dashboard/subscriptions?ownerType=individual&userId=${user.id}`
              );
            }}
            sx={{ color: 'text.secondary' }}
          >
            <ListItemIcon>
              <Iconify icon="eva:clock-outline" width={24} height={24} />
            </ListItemIcon>
            <ListItemText primary="Ver todas sus suscripciones" primaryTypographyProps={{ variant: 'body2' }} />
          </MenuItem>
        </Tooltip>
        <Divider />
        <Tooltip title="Estamos migrando el borrado de usuarios. Temporalmente deshabilitado." placement="left">
          <span>
            <MenuItem disabled sx={{ color: 'text.secondary' }}>
              <ListItemIcon>
                <Iconify icon="eva:person-delete-outline" width={24} height={24} />
              </ListItemIcon>
              <ListItemText primary="Eliminar usuario" primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
}
